import {
	Button,
	Typography,
	Box,
	Alert,
	DataTable,
	BDSProvider,
	Card,
	CardActions,
	CardContent,
	CardHeader,
	Container,
	MenuItem,
	Select,
} from '@barracuda-internal/bds-core-v2';
import React, { ReactElement, useState, useMemo, useCallback } from 'react';
import { useApiRequest } from 'src/hooks/useApi';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import useAuth from 'src/hooks/useAuth';
import { roleMap } from 'src/routes/settings/users/roleMap';
import { UserAssignment, UserDetail } from 'src/types/dataTypes';
import { isBCCRole } from 'src/utils/auth';

interface Props {
	onCancel: () => void;
	onChange: (userDetail: UserDetail) => void;
	userDetail: UserDetail;
}

function EditUserDialog({
	onCancel,
	onChange,
	userDetail,
}: Props): ReactElement {
	const { api } = useAppToolchain();
	const { auth } = useAuth();

	const [doDelete, setDoDelete] = useState<UserAssignment>();
	const [localUserDetail, setLocalUserDetail] =
		useState<UserDetail>(userDetail);

	const assignedRoles = localUserDetail.assignments.filter((a) =>
		a.domain.startsWith(`/accounts/${auth.accountDetails.account.id}`),
	);

	const unassignedRoles = Object.keys(roleMap).filter(
		(rm) => !assignedRoles.some((ar) => ar.role === rm) && !isBCCRole(rm),
	);

	const handleAssignment = (assignment: UserAssignment) => {
		localUserDetail.assignments.push(assignment);
		setLocalUserDetail({
			...localUserDetail,
		});
		onChange(localUserDetail);
	};

	const handleDelete = useCallback((role: string) => {
		const deleteRoles = assignedRoles.filter((r) => r.role === role);
		if (deleteRoles.length) {
			setDoDelete(deleteRoles[0]);
		}
	}, [assignedRoles]);

	useApiRequest(
		!!doDelete &&
			((signal) =>
				api.deleteUserAssignment(
					auth.accountDetails.account.id,
					doDelete,
					{
						signal,
					},
				)),
		[doDelete],
		([status, , err]) => {
			if (status === 'RESOLVED' && !err) {
				const newLocalDetail = {
					...localUserDetail,
					assignments: localUserDetail.assignments.filter(
						(a) => a.role !== doDelete.role,
					),
				};
				setLocalUserDetail(newLocalDetail);
				onChange(newLocalDetail);
			}
			setDoDelete(null);
		},
	);

	const columns = useMemo(
		() => [
			{
				accessorKey: 'role',
				header: 'Role',
				enableColumnActions: false,
				enableSorting: false,
				Cell: ({ row }: { row: { original: { role: string } } }) => (
					<Typography>{roleMap[row.original.role]}</Typography>
				),
			},
			{
				accessorKey: 'actions',
				header: '',
				size: 150,
				enableColumnActions: false,
				enableSorting: false,
				muiTableBodyCellProps: {
					align: 'right' as const,
				},
				Cell: ({ row }: { row: { original: { role: string } } }) =>
					!isBCCRole(row.original.role) ? (
						<Button
							onClick={() => handleDelete(row.original.role)}
							color="secondary"
						>
							Remove
						</Button>
					) : null,
			},
		],
		[handleDelete],
	);

	return (
		<Container>
			<Card style={{ margin: 16 }}>
				<CardHeader
					title={userDetail.user.name}
					subheader={userDetail.user.email}
				/>
				<CardContent>
					<Typography variant="h6">Permissions</Typography>
					<Alert severity="info" sx={{ mb: 2 }}>
						User roles originate from Barracuda Cloud Control and are
						modifiable within the Barracuda Cloud Control System.
					</Alert>
					<Box sx={{ mb: 2 }}>
						<RoleSelector
							unassignedRoles={unassignedRoles}
							userDetail={localUserDetail}
							onSave={handleAssignment}
						/>
					</Box>
					<BDSProvider>
						<DataTable
							layoutMode="semantic"
							columns={columns}
							data={assignedRoles}
							enableTopToolbar={false}
							enableBottomToolbar={false}
							enableFilters={false}
							enablePagination={false}
							enableColumnActions={false}
							enableSorting={false}
							enableTableHead={false}
							enableGlobalFilter={false}
							showSearchFields={false}
							muiTableBodyCellProps={{ sx: { padding: '12px' } }}
						/>
					</BDSProvider>
				</CardContent>
				<CardActions>
					<Button color="secondary" onClick={onCancel}>
						Close
					</Button>
				</CardActions>
			</Card>
		</Container>
	);
}

function RoleSelector({
	onSave,
	unassignedRoles,
	userDetail,
}: {
	onSave: (result: UserAssignment) => void;
	unassignedRoles: string[];
	userDetail: UserDetail;
}) {
	const { api } = useAppToolchain();
	const { auth } = useAuth();

	const [doSave, setDoSave] = useState(false);
	const [selectedRole, setSelectedRole] = useState('');

	const [saveStatus] = useApiRequest(
		() =>
			doSave &&
			api.addUserAssignment(auth.accountDetails.account.id, {
				role: selectedRole,
				subject: userDetail.user.id,
			}),
		[doSave],
		([status, res, err]) => {
			setDoSave(false);
			if (status === 'RESOLVED' && !err && res) {
				setSelectedRole('');
				onSave(res);
			}
		},
	);

	const handleChange = (e) => setSelectedRole(e.target.value);
	const handleSave = () => {
		setDoSave(true);
	};

	if (!unassignedRoles.length) return null;

	return (
		<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2 }}>
			<Select
				value={selectedRole}
				onChange={handleChange}
				displayEmpty
				variant="outlined"
				style={{ minWidth: 150, height: 36 }}
				MenuProps={{
					PaperProps: {
						style: { maxHeight: 200 },
					},
				}}
			>
				<MenuItem value="" disabled style={{ minHeight: 32, fontSize: 14 }}>
					Choose A Role
				</MenuItem>
				{unassignedRoles.map((role) => (
					<MenuItem value={role} key={role} style={{ minHeight: 32, fontSize: 14 }}>
						{roleMap[role]}
					</MenuItem>
				))}
			</Select>
			<Button
				onClick={handleSave}
				isLoading={saveStatus === 'RUNNING'}
				disabled={saveStatus === 'RUNNING' || !selectedRole}
				variant="contained"
				color="primary"
			>
				Add
			</Button>
		</Box>
	);
}

export default EditUserDialog;
